import './style.css';
import './room-panel.css';
import './auth.css';
import './radial-menu.css';
import { Connection } from './network';
import { Viewport } from './viewport';
import { RoomPanel } from './room-panel';
import { AuthClient, AuthPanel } from './auth';
import { SocialPanel } from './social';
import { AssetPanel } from './asset-panel';
import { BuildingPanel } from './building';
import { InteractionPanel } from './interactions';
import { WorldMenu } from './menu';
import { ContextActions } from './context-actions';
import { WindowLayout } from './window-layout';
import { EnvironmentImporter } from './environment-import';
import { FPSOverlay } from './fps-overlay';
import { GraphicsPanel } from './graphics-panel';
import { loadGraphicsSettings } from './graphics-settings';
import { LightingPanel } from './lighting-panel';
import type { CameraPreset } from './camera';
import { STEP_SECONDS } from './movement';
import { advanceEntity, type Direction } from './prediction';
import type { Entity, WorldMap } from './protocol';
import defaultMap from '../../protocol/map.json';
import {initializeWorldTransport} from './world-transport';
import {HomeLink} from './home-link';

await initializeWorldTransport();

const element = <T extends HTMLElement>(id: string): T => { const el = document.getElementById(id); if (!el) throw new Error(`Missing ${id}`); return el as T; };
const canvas = element<HTMLCanvasElement>('world');
const status = element('status'), details = element('details'), overlay = element('overlay'), diagnostics = element('diagnostics');
const viewport = new Viewport(canvas, loadGraphicsSettings());
const fpsOverlay = new FPSOverlay([...document.querySelectorAll<HTMLInputElement>('[data-fps-toggle]')], element('fps-overlay'));
const graphicsPanel = new GraphicsPanel(viewport, stopInput);
const local = new URLSearchParams(location.search).has('local');
const frameDiagnostics = new URLSearchParams(location.search).has('diagnostics');
const keys = new Set<string>();
const menu = new WorldMenu(stopInput, () => viewport.releasePointerLock());
const recognized = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
const connection = new Connection(refresh, () => { if (!local) void auth.refresh().catch(() => {}); }, () => { stopInput(); viewport.environmentChanged(); });
connection.roomID = new URLSearchParams(location.search).get('room') || 'lobby';
let roomPanel: RoomPanel | undefined;
let authPanel: AuthPanel | undefined;
let homeLink:HomeLink|undefined;
let socialPanel: SocialPanel | undefined;
let assetPanel: AssetPanel | undefined;
let buildingPanel: BuildingPanel | undefined;
let interactionPanel: InteractionPanel | undefined;
let contextActions: ContextActions | undefined;
let environmentImporter: EnvironmentImporter | undefined;
let lightingPanel: LightingPanel | undefined;
let sessionKey = '';
let principalKey = '';
const auth = new AuthClient(() => {
  const key = auth.state?.csrf ?? '';
  const user = auth.state?.user;
  const principal = user ? JSON.stringify([user.id, user.role, user.displayName, user.muted]) : '';
  if (key !== sessionKey) {
    sessionKey = key; stopInput(); connection.disconnect(); viewport.resetAssets(); roomPanel?.authChanged(); socialPanel?.reset(); assetPanel?.authChanged(); environmentImporter?.authChanged();
    if (auth.state?.user) connection.connect();
  } else if (principal !== principalKey) { viewport.resetAssets(); roomPanel?.authChanged(); socialPanel?.reset(); assetPanel?.authChanged(); environmentImporter?.authChanged(); }
  else roomPanel?.refreshAccess();
  principalKey = principal;
  authPanel?.render(); homeLink?.render(); refresh();
});
const localEntity: Entity = { id: 'local', position: { ...defaultMap.spawn }, rotation: [0, 0, 0, 1], moving: false, ack: 0, motion: 0 };
const localEntities = new Map([[localEntity.id, localEntity]]);
let presentation = new Map<string, Entity>();
let frames: number[] = [], previous = performance.now(), animationFrame = 0, running = true;
function refresh(): void {
  const entities = local ? localEntities : connection.state.entities;
  status.textContent = local ? 'Local controller harness' : connection.status;
  status.dataset.connected = String(local || connection.status.startsWith('Connected'));
  const self = entities.get(local ? 'local' : connection.state.selfId);
  details.textContent = `${entities.size} nearby avatar${entities.size === 1 ? '' : 's'} · ${self ? `position ${self.position.x.toFixed(2)}, ${self.position.z.toFixed(2)}` : 'waiting for a room snapshot'}`;
  overlay.hidden = entities.size > 0; overlay.textContent = connection.status;
  const estimate = connection.clock.estimate;
  const render = viewport.diagnostics;
  const sorted = [...frames].sort((a, b) => a - b);
  diagnostics.textContent = `${local ? 'OFFLINE' : 'PREDICTED · SERVER AUTHORITY'} · tick ${connection.state.tick} · ${render.calls} draws · ${estimate ? `clock ±${estimate.uncertainty.toFixed(1)}ms (${estimate.samples} samples)` : 'clock synchronizing'} · ${connection.diagnostics.pendingInputs} pending · frame p95 ${(sorted[Math.floor(sorted.length * .95)] ?? 0).toFixed(1)}ms`;
  // Explicit read-only diagnostics enable resource/performance checks without
  // exposing Three.js roots, a mutable room model, or a command backdoor.
  diagnostics.dataset.snapshot = JSON.stringify({
    epoch: connection.state.epoch, selfId: local ? 'local' : connection.state.selfId, tick: connection.state.tick,
    entities: [...entities.values()], render, clock: estimate, frameP95: sorted[Math.floor(sorted.length * .95)] ?? 0,
    presentation: [...presentation.values()], network: connection.diagnostics,
    content: connection.state.content,
    edits: [...connection.edits.values()], presentedObjects: buildingPanel?.presentation() ?? [],
    interactions: connection.interactions,
  });
  roomPanel?.render(environmentImporter?.busy ?? false);
  environmentImporter?.render();
  socialPanel?.render();
  assetPanel?.render();
  buildingPanel?.render();
  interactionPanel?.render();
  contextActions?.render();
  graphicsPanel.refresh();
  lightingPanel?.refresh();
  const invitation = connection.ownInteraction;
  menu.render(!local && !!auth.state?.user, !!connection.state.epoch, invitation?.phase === 'invited' && invitation.roles[1]?.entityId === connection.state.selfId ? invitation.id : '');
  element('mouselook-hint').hidden = viewport.cameraPreset !== 'mouselook';
  for (const button of document.querySelectorAll<HTMLButtonElement>('[data-camera-preset]')) {
    button.setAttribute('aria-pressed', String(button.dataset.cameraPreset === viewport.cameraPreset)); button.disabled = !self;
  }
  for (const id of ['camera-zoom-in', 'camera-zoom-out']) element<HTMLButtonElement>(id).disabled = !self || viewport.cameraPreset === 'mouselook';
}
function turn(): number { return Number(keys.has('KeyD') || keys.has('ArrowRight')) - Number(keys.has('KeyA') || keys.has('ArrowLeft')); }
function direction(): Direction {
  if (viewport.dragging) return { x: 0, z: 0 };
  const forward = Number(keys.has('KeyW') || keys.has('ArrowUp')) - Number(keys.has('KeyS') || keys.has('ArrowDown'));
  return forward || turn() ? viewport.movementDirection(forward) : { x: 0, z: 0 };
}
function stopInput(): void { keys.clear(); if (!local) connection.stop(); }
const onKeyDown = (event: KeyboardEvent): void => {
  if (event.code === 'Home' && event.target === canvas && !event.ctrlKey && !event.metaKey && !event.altKey) { event.preventDefault(); viewport.resetCamera(); return; }
  if (event.target instanceof HTMLElement && event.target.closest('.floating-panel, dialog, .radial-menu')) return;
  if (!recognized.has(event.code) || event.ctrlKey || event.metaKey || event.altKey || event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLButtonElement || event.target instanceof HTMLSelectElement) return;
  event.preventDefault(); keys.add(event.code);
};
// Normal release drains predicted steps; cancellation would roll back movement
// already shown locally. Blur/background/recovery still use stop barriers.
const onKeyUp = (event: KeyboardEvent): void => { keys.delete(event.code); };
const onVisibility = (): void => {
  keys.clear();
  if (!local) { if (document.hidden) connection.suspend(); else connection.resume(); }
  previous = performance.now();
  viewport.frameBudget.reset();frames=[];
};
window.addEventListener('keydown', onKeyDown); window.addEventListener('keyup', onKeyUp); window.addEventListener('blur', stopInput); document.addEventListener('visibilitychange', onVisibility);
const reconnect = element<HTMLButtonElement>('reconnect'); reconnect.hidden = local;
reconnect.addEventListener('click', () => { if (auth.state?.user) { stopInput(); connection.connect(); canvas.focus(); } });
element('camera-reset').addEventListener('click', () => viewport.resetCamera());
element('camera-zoom-in').addEventListener('click', () => viewport.zoomCamera(.8));
element('camera-zoom-out').addEventListener('click', () => viewport.zoomCamera(1.25));
for (const button of document.querySelectorAll<HTMLButtonElement>('[data-camera-preset]')) button.addEventListener('click', () => {
  stopInput();
  const preset = button.dataset.cameraPreset as CameraPreset;
  if (preset === 'mouselook') menu.close('camera-panel');
  viewport.setCameraPreset(preset); refresh();
});
const interval = setInterval(() => {
  const d = document.hidden ? { x: 0, z: 0 } : direction();
  if (local) {
    Object.assign(localEntity, advanceEntity(localEntity, d, STEP_SECONDS, defaultMap as WorldMap));
  }
  refresh();
}, 50);
function render(now: number): void {
  if (!running) return;
  const dt = now - previous; previous = now;
  if (!document.hidden) viewport.turnCamera(turn(), Math.min(Math.max(dt / 1000, 0), .1));
  presentation = local ? localEntities : connection.frame(now, direction());
  viewport.sync(presentation, local ? 'local' : connection.state.selfId, local ? defaultMap as WorldMap : connection.state.map, local ? null : connection.state.members, local ? 'flat-v1' : connection.state.content?.room.environment ?? 'flat-v1');
  viewport.syncObjects(connection.state.content?.room.id ?? '', buildingPanel?.presentation() ?? connection.state.content?.objects ?? []);
  viewport.syncInteractions(connection.interactions);
  connection.reportReadiness(i => viewport.interactionReadiness(i));
  // After a long shader/upload pause, let queued state/stop replies recover
  // before issuing more GPU work. Prediction/network pumping above still runs.
  const drawDelta=!local&&connection.resynchronizing?null:viewport.beginFrame(now,!document.hidden);
  if(drawDelta!==null) {
    viewport.render(drawDelta, connection.serverTime(now), connection.interactions);fpsOverlay.frame(now);
    if(drawDelta>0) {frames.push(viewport.frameBudget.lastFrameMS);if(frames.length>240)frames.shift();}
  }
  animationFrame = requestAnimationFrame(render);
  if (frameDiagnostics) canvas.dataset.presentation = JSON.stringify([...presentation.values()]);
}
function dispose(): void {
  fpsOverlay.dispose();
  graphicsPanel.dispose();
  lightingPanel?.dispose();
  running = false; cancelAnimationFrame(animationFrame); clearInterval(interval); environmentImporter?.dispose(); windowLayout.dispose(); contextActions?.dispose(); menu.dispose(); buildingPanel?.dispose(); connection.disconnect(); viewport.dispose(); frames = [];
  window.removeEventListener('keydown', onKeyDown); window.removeEventListener('keyup', onKeyUp); window.removeEventListener('blur', stopInput); document.removeEventListener('visibilitychange', onVisibility);
}
window.addEventListener('pagehide', dispose, { once: true });
window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
if (import.meta.hot) import.meta.hot.dispose(dispose);
if (!local) {
  authPanel = new AuthPanel(auth);
  homeLink=new HomeLink(auth);
  roomPanel = new RoomPanel(connection, id => { stopInput(); const url = new URL(location.href); url.searchParams.set('room', id); history.replaceState(null, '', url); connection.connect(id); canvas.focus(); }, auth);
  socialPanel = new SocialPanel(auth, connection);
  assetPanel = new AssetPanel(auth, connection, () => menu.open('interactions-panel'));
  buildingPanel = new BuildingPanel(connection, auth, viewport, () => menu.open('build-panel', false));
  interactionPanel = new InteractionPanel(connection, auth);
  contextActions = new ContextActions(connection, auth, viewport, buildingPanel, menu, canvas, stopInput);
  environmentImporter = new EnvironmentImporter(connection, auth, stopInput);
  void auth.refresh().catch(error => { element('auth-message').textContent = error instanceof Error ? error.message : 'Account service unavailable'; });
} else { element('room-tools').hidden = true; element('auth-overlay').hidden = true; element('account-strip').hidden = true; }
const windowLayout = new WindowLayout(element('stage'), stopInput);
lightingPanel = new LightingPanel(viewport,connection,auth,local,stopInput);
refresh(); animationFrame = requestAnimationFrame(render);
